import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import { getStarterUpgradeProduct } from 'pricing-pages-lib/utils/getUpgradeProduct';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import Raven from 'raven-js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import BannerSection from '../components/banners/BannerSection';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import IntegrationsSection from '../components/common/IntegrationsSection';
import FailedLoadBanner from '../components/common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import PhoneSupport from '../components/common/PhoneSupport';
import PhoneSupportFooter from '../components/common/PhoneSupportFooter';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import ValuePropsSection from '../components/common/ValuePropsSection';
import Faq from '../components/faqs/Faq';
import FooterMicrocopy from '../components/faqs/FooterMicrocopy';
import Question from '../components/faqs/Question';
import ProductFeatureTable from '../components/feature-table/ProductFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { HorizontalFreeCard } from '../components/merch-card/HorizontalFreeCard';
import HubTierInfo from '../components/merch-card/HubTierInfo';
import { OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { FaqsConfig } from '../constants/faqs/FaqsConfig';
import { getMerchCardsToShow, getMerchCardsToShowAll, getMerchCardsToShowProEnterpriseOnly } from '../constants/merchCards/MerchCardsToShow';
import { STARTER } from '../constants/routing/Tabs';
import { SCREENS } from '../constants/tracking/Screens';
import { TRACKING_SOURCES } from '../constants/tracking/TrackingSources';
import { ProductPageContext } from '../context/ProductPageContext';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePageParams } from '../hooks/usePageParams';
import { useTrackRetail } from '../hooks/useTrackRetail';
import { useAppSelector } from '../reducers';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { navItemForProductLineSelector } from '../selectors/RoutingSelectors';
import { useSSM0009Experiment, useSSM0009ExperimentEffect } from '../utils/experiments/ssm0009';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(productLine.toLowerCase());
  const screen = SCREENS[productLine];
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const {
    tab
  } = usePageParams();
  const {
    isSSM0009Variant,
    isSSM0009HideStarter
  } = useSSM0009Experiment();
  useEffect(() => {
    if (!screen) {
      Raven.captureMessage('ProductPage screen is undefined', {
        extra: {
          productLine
        }
      });
      return;
    }
    trackPageView({
      screen,
      tab
    });
  }, [productLine, screen, tab]);
  const highlightedProducts = getHighlightsForProductLine(productLine);
  const getProductLevels = () => {
    if (isSSM0009Variant) {
      if (isSSM0009HideStarter) {
        return getMerchCardsToShowProEnterpriseOnly();
      }
      return getMerchCardsToShowAll();
    }
    return getMerchCardsToShow({
      tab
    });
  };
  const productLevels = getProductLevels();
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  useSSM0009ExperimentEffect();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const navItem = useAppSelector(state => navItemForProductLineSelector(state, productLine));
  const faqsConfig = FaqsConfig[navItem];
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }
  const upgradeProduct = getStarterUpgradeProduct({
    productLine,
    tab
  });
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
      name: `${productLine}_PAGE`
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: navItem,
      children: /*#__PURE__*/_jsx(ProductInfoContainer, {
        children: /*#__PURE__*/_jsxs(ProductPageContext.Provider, {
          value: {
            highlightedProducts: filteredHighlightedProducts
          },
          children: [/*#__PURE__*/_jsx(BannerSection, {
            productLine: productLine,
            screen: screen
          }), /*#__PURE__*/_jsx(FailedLoadBanner, {
            hasFailed: hasPricingAndPurchasingFailed
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              productLine: productLine
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              productLine: productLine
            })]
          }), /*#__PURE__*/_jsx(HubTierInfo, {
            productLine: productLine,
            selectedTab: tab
          }), isSSM0009Variant && !isSSM0009HideStarter && /*#__PURE__*/_jsx(HorizontalFreeCard, {
            productLine: productLine
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(ProductFeatureTable, {
              productLine: productLine,
              highlightedProducts: filteredHighlightedProducts,
              screen: screen,
              tab: tab
            }, `${productLine}-${tab}`)
          }), /*#__PURE__*/_jsx(PageSection, {
            children: /*#__PURE__*/_jsx(PhoneSupport, {
              phoneSourceId: TRACKING_SOURCES[`${productLine}_MIDDLE`] || TRACKING_SOURCES.PRODUCT_PAGE,
              screen: screen,
              upgradeProduct: upgradeProduct
            })
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab: isSSM0009Variant ? STARTER : tab
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/case-studies/CaseStudies`),
            publicComponentProps: {
              productLine,
              tab
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsxs(Faq, {
            children: [faqsConfig.map(_ref => {
              let {
                  questionKey
                } = _ref,
                passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: TRACKING_SOURCES[`${productLine}_FOOTER`] || TRACKING_SOURCES.PRODUCT_PAGE,
            screen: screen,
            upgradeProduct: upgradeProduct
          })]
        })
      })
    })]
  });
};
export default ProductPage;